import { FC } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SignOff, Country } from "../../../../api/Api";
import { countryKeys, useUpdateCountry } from "../../dataHooks/countryQueries";
import { SignOffPageLayout } from "../../../../components/lifecycle/signOff/signOffPage";
import { SignOffDetailsPanel } from "./signOffDetailsPanel";
import { buildSignOffPdf } from "./buildSignOffPdf";

type Props = {
  specificCountry: Country;
};

export const SignOffPage: FC<Props> = ({ specificCountry }) => {
  const updateCountry = useUpdateCountry();
  const queryClient = useQueryClient();

  const persist = (
    signOff: SignOff,
    handlers: { onError: (e: Error) => void; onSuccess: () => void }
  ) => {
    updateCountry.mutate(
      { ...specificCountry, signOff },
      {
        onError: (e) => handlers.onError(e as Error),
        onSuccess: () => {
          handlers.onSuccess();
          // The backend may have mutated the sign-off (e.g. flipping status to "completed" once
          // all signatories are signed). The shared useUpdateCountry hook seeds the cache with
          // the request body, so without an explicit invalidation we'd keep showing our
          // optimistic state until the user reloads.
          if (specificCountry.id) {
            queryClient.invalidateQueries({
              queryKey: countryKeys.specific(specificCountry.id)
            });
          }
        }
      }
    );
  };

  const handleDownloadPdf = (displayedSignOff?: SignOff | null) => {
    const doc = buildSignOffPdf({
      ...specificCountry,
      signOff: displayedSignOff ?? specificCountry.signOff
    });
    const safeName = (specificCountry.countryName || "country").replace(
      /[^a-z0-9]/gi,
      "_"
    );
    doc.save(`signoff_${safeName}.pdf`);
  };

  return (
    <SignOffPageLayout
      entityName={specificCountry.countryName}
      signOff={specificCountry.signOff}
      vp={specificCountry.vp}
      saving={updateCountry.isPending}
      persist={persist}
      detailsPanel={<SignOffDetailsPanel country={specificCountry} />}
      onDownloadPdf={handleDownloadPdf}
    />
  );
};
